<template>
  <div class="relative">
    <!-- Reserves the collapsed button footprint so the nav row doesn't jump
         when the card below is taken out of flow (absolute). -->
    <div class="h-10 w-16 md:h-13 md:w-[116px]" aria-hidden="true" />

    <!-- Click-outside catcher while open. -->
    <div
      v-if="uiStore.showMenu"
      class="fixed inset-0"
      @click="uiStore.showMenu = false"
    />

    <!-- Morphing card: the menu button (collapsed) expands into the panel. -->
    <div
      class="absolute top-0 left-0 overflow-hidden rounded-[26px] bg-white shadow-[0_4px_20px_0_rgba(0,0,0,0.15)] transition-[width,height] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
      :style="{
        width: `${uiStore.showMenu ? openWidth : collapsedWidth}px`,
        height: `${uiStore.showMenu ? openHeight : collapsedHeight}px`,
      }"
    >
      <!-- Border overlay instead of a border on the card itself, so the
           stroke doesn't change the box the measured width/height animate. -->
      <div
        class="pointer-events-none absolute inset-0 rounded-[26px] border-2 transition-colors duration-200"
        :class="
          bordered && !uiStore.showMenu
            ? 'border-black/10'
            : 'border-transparent'
        "
      />

      <!-- Fixed-width content so its measured height is stable regardless of
           the animated card width; the card just clips it while collapsed. -->
      <div ref="content" class="w-[344px] max-w-[calc(100vw-4rem)]">
        <button
          ref="header"
          type="button"
          class="flex h-10 items-center gap-x-2.5 px-5 text-[16px] md:h-13"
          @click="uiStore.showMenu = !uiStore.showMenu"
        >
          <div class="flex flex-none flex-col space-y-1">
            <div
              v-for="n in 2"
              :key="n"
              class="h-[3px] w-6 rounded-full bg-black transition-transform duration-200"
              :class="
                uiStore.showMenu
                  ? n === 1
                    ? 'translate-y-[3.5px]'
                    : 'translate-y-[-3.5px]'
                  : ''
              "
            />
          </div>
          <span class="hidden font-medium md:inline-block">Menu</span>
        </button>

        <div>
          <div class="h-px w-full bg-black/20" />

          <nav class="flex flex-col gap-y-4 px-8 pt-8">
            <MenuNavLink
              v-for="item in navItems"
              :key="item.label"
              :to="useNavLinkUri(item)"
            >
              {{ item.label }}
            </MenuNavLink>
          </nav>

          <div class="px-8 pt-8 pb-8">
            <p
              class="text-[13px] font-medium tracking-[0.01em] text-black/50 uppercase"
            >
              Programs
            </p>
            <MenuProgramCard
              class="mt-4"
              :to="getSaPath()"
              label="South Australia"
              icon="/images/sa-program-icon.svg"
            />

            <!-- Get involved lives here only where the nav bar hides it (below xl). -->
            <NuxtLink
              to="/get-involved"
              class="btn btn-sm mt-10 h-[52px] w-full gap-x-2.5 border border-black/20 bg-white text-[16px] hover:bg-white xl:hidden"
              @click="uiStore.showMenu = false"
            >
              <SvgGlyphSupport />
              <span>Get involved</span>
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getSaPath } from 'shared'

defineProps<{ bordered?: boolean }>()

const uiStore = useUIStore()
const globalsStore = useGlobalsStore()

// The "South Australia" entry lives in the Programs card, not the nav list.
const navItems = computed(() =>
  (globalsStore.settings?.menu?.items ?? []).filter(
    (item) => item.label !== 'South Australia',
  ),
)

const content = ref<HTMLElement>()
const header = ref<HTMLElement>()
const collapsedWidth = ref(116)
const collapsedHeight = ref(52)
const openWidth = ref(344)
const openHeight = ref(0)

// Observe both boxes so the animated card always tracks its real content size
// instead of a snapshot — no stale height (and bottom gap) when fonts load,
// data arrives, or the layout changes. The header's transform (hamburger bars)
// doesn't affect layout, so its measured size is the same open or closed.
useResizeObserver(header, () => {
  if (!header.value) return
  collapsedWidth.value = header.value.offsetWidth
  collapsedHeight.value = header.value.offsetHeight
})

useResizeObserver(content, () => {
  if (!content.value) return
  openWidth.value = content.value.offsetWidth
  openHeight.value = content.value.scrollHeight
})
</script>
